import React, { useEffect, useState } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { useIsMutating } from 'react-query';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { showOPLConfirmationDialog } from '../../helpers/showOPLConfirmationDialog';
import { TaxesDTO, TaxEstimationMode, useTaxesQuery } from './api/useTaxesQuery';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { Wrapper } from '../components/Wrapper/Wrapper';
import { ProfilePageHeader } from '../components/ProfilePageHeader/ProfilePageHeader';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { TaxesLoading } from './Taxes.loading';
import { isAdviserSelector } from '../../App/AppSelector';
import { hasAccessToEditProfileSelector } from '../../CommonSelectors/CommonAreaSelector';
import { dataToForm, FORM_ID, formToBody } from './Taxes.meta';
import { TaxesForm } from './components/TaxesForm/TaxesForm';
import { Checkbox } from '../../CommonComponents/OPL/Checkbox/Checkbox';
import { SAVA_TAXES_MK, useSaveTaxesMutation } from './api/useSaveTaxesMutation';
import { StickyFooter } from '../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { toast } from '../../CommonComponents/OPL/Toast/Toast';
import { Alert } from '../../CommonComponents/OPL/Alert/Alert';
import { ItemizedDeductions } from './components/ItemizedDeductions/ItemizedDeductions';
import { Box } from '../../CommonComponents/OPL/Box/Box';

interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
}

export const Taxes = ({ clientId, workflowProps }: Props) => {
  useResetModuleWrapperStyles({ isActive: !workflowProps });

  const { isLoading, isError } = useTaxesQuery({
    clientId,
    stepId: workflowProps?.stepId,
  });

  const isSavingTaxes = useIsMutating({ mutationKey: SAVA_TAXES_MK }) > 0;

  const isAdvisorArea = useSelector(isAdviserSelector);
  const canEdit = useSelector(hasAccessToEditProfileSelector);

  useEffectOnce(() => {
    const helpKey = ADV.util.setHelp(ADV.HelpDescriptors.TaxFilingStep);

    return () => {
      ADV.util.unsetHelp(helpKey);
    };
  });

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper hasStickyFooter isWorkflow={!!workflowProps}>
        <ProfilePageHeader
          title="Taxes"
          description={
            isAdvisorArea ? 'How does the client file their taxes?' : 'How do you file your taxes?'
          }
        />

        <TaxesContent clientId={clientId} workflowProps={workflowProps} readOnly={!canEdit} />
      </Wrapper>

      {isLoading || isError ? (
        <StickyFooter />
      ) : workflowProps ? (
        <WorkflowFooter
          isSubmit
          form={FORM_ID}
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          isOnNextLoading={isSavingTaxes}
        />
      ) : (
        canEdit && (
          <StickyFooter fullWidthChildren maxContentWidth={800} mt={0}>
            <Button
              type="submit"
              form={FORM_ID}
              isLoading={isSavingTaxes}
              size="large"
              ml="auto"
              mr="16"
            >
              Save
            </Button>
          </StickyFooter>
        )
      )}
    </>
  );
};

const TaxesContent = ({ clientId, workflowProps, readOnly }: Props & { readOnly: boolean }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { isLoading, isError, refetch, data, dataUpdatedAt } = useTaxesQuery({
    clientId,
    stepId: workflowProps?.stepId,
  });

  if (isLoading) {
    return <TaxesLoading />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} mx="auto" my="56" />;
  }

  if (!data) {
    return null;
  }

  return (
    <TaxesFormContent
      // Re-render the form after saving and updating the query
      key={`TaxesFormContent-key--${dataUpdatedAt}`}
      clientId={clientId}
      workflowProps={workflowProps}
      data={data}
      showAdvanced={showAdvanced}
      toggleAdvanced={() => setShowAdvanced(!showAdvanced)}
      readOnly={readOnly}
    />
  );
};

interface FormProps {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
  data: TaxesDTO;
  showAdvanced: boolean;
  toggleAdvanced(): void;
  readOnly: boolean;
}

const TaxesFormContent = ({
  clientId,
  workflowProps,
  data,
  showAdvanced,
  toggleAdvanced,
  readOnly,
}: FormProps) => {
  const formBag = useForm({
    defaultValues: dataToForm(data),
    disabled: readOnly,
  });

  const saveTaxesMutation = useSaveTaxesMutation({ clientId, stepId: workflowProps?.stepId });

  const isFormDirty = formBag.formState.isDirty;

  useEffect(() => {
    if (isFormDirty) {
      ADV.util.Router.registerNavigateAwayCallback(showOPLConfirmationDialog);
    } else {
      ADV.util.Router.unregisterNavigateAwayCallback();
    }
  }, [isFormDirty]);

  const onSubmit = formBag.handleSubmit(async values => {
    try {
      const body = formToBody(values);

      await saveTaxesMutation.mutateAsync({ clientId, body });

      formBag.reset(dataToForm({ ...data, ...body }));

      if (workflowProps) {
        workflowProps.onNext();
      } else {
        toast.success('Taxes information was successfully saved.');
      }
    } catch (error) {
      console.error(error);
    }
  });

  const [
    useDifferentPostRetirementTaxes,
    preRetirementTaxEstimationMode,
    postRetirementTaxEstimationMode,
  ] = formBag.watch([
    'useDifferentPostRetirementTaxes',
    'preRetirementTaxEstimationMode',
    'postRetirementTaxEstimationMode',
  ]);

  /**
   * Determines whether to hide the advanced tax section.
   * Returns true when using simplified tax calculations:
   * - When pre-retirement uses effective tax rate AND either:
   *   - Post-retirement also uses effective tax rate, or
   *   - Post-retirement taxes aren't being handled separately
   */
  const hideAdvancedSection =
    preRetirementTaxEstimationMode === TaxEstimationMode.UseEffectiveTaxRate &&
    (postRetirementTaxEstimationMode === TaxEstimationMode.UseEffectiveTaxRate ||
      !useDifferentPostRetirementTaxes);

  return (
    <FormProvider {...formBag}>
      {saveTaxesMutation.error && (
        <Alert message={saveTaxesMutation.error.message} variant="error" mb="16" />
      )}

      <Box
        as="form"
        id={FORM_ID}
        onSubmit={onSubmit}
        display="flex"
        flexDirection="column"
        style={{ gap: 24 }}
      >
        <TaxesForm data={data} />

        <Checkbox {...formBag.register('useDifferentPostRetirementTaxes')}>
          Use different tax rate for retirement
        </Checkbox>

        {useDifferentPostRetirementTaxes && <TaxesForm isPostRetirement data={data} />}

        {!hideAdvancedSection && (
          <>
            <Button
              variant="link"
              leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
              onClick={toggleAdvanced}
              aria-expanded={showAdvanced}
              size="small"
              mr="auto"
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced
            </Button>

            {showAdvanced && (
              <Checkbox
                {...formBag.register('useStandardDeductions')}
                helperText="By selecting this option, the standard deduction will be used"
              >
                Do not use Itemized Deductions
              </Checkbox>
            )}

            {showAdvanced && <ItemizedDeductions />}
          </>
        )}
      </Box>
    </FormProvider>
  );
};
